import { useAppConfig } from 'application/configuration'
import { useStore } from 'effector-react'
import { useListSmartContractAPI } from 'features/smart-contract/list/useListSmartContractAPI'
import { useEditTradeInText } from 'features/smart-contract/utils'
import { usePriceFormatter } from 'lib/use-price-formatter'
import { FC } from 'react'
import { useAVNConfig } from 'stages/AVN'
import styled from 'styled-components'
import { SkeletonBlock } from 'ui/skeleton-block'
import { Switch } from 'ui/switch'

import { getProjectName } from '../../../lib/get-project-name'
import { $chosenCreditFilterValues, $creditFilterEnabled, setCreditFilterEnabled } from '../../credit-filter-form/model'
import { GenericV2Sort } from './GenericV2Sort'

export type Props = {
  openCreditFilterForm: () => void
}

export const GenericV2CommercialFilters: FC<Props> = ({ openCreditFilterForm }) => {
  const {
    creditFilterForm: { i18n: creditFilterFormI18n },
    sorting,
    i18n: carsListI18n,
    smartContractInstalled: useSmartContract,
    isCreditFilterVisible,
    isTradeInVisible
  } = useAVNConfig()
  const { state, api, creditAvailable, tradeInAvailable } = useListSmartContractAPI()
  const smartContractI18n = useAppConfig().smartContract.i18n
  const creditFilterEnabled = useStore($creditFilterEnabled)
  const editTradeInText = useEditTradeInText({ state, i18n: smartContractI18n })
  const appliedCreditFilterValues = useStore($chosenCreditFilterValues)
  const commonI18n = useAppConfig().i18n.common
  const formatPrice = usePriceFormatter()

  return (
    <Wrapper>
      <FiltersContainer>
        {useSmartContract && (
          <>
            {state
              ? creditAvailable &&
                isCreditFilterVisible && (
                  <SwitchWrapper>
                    <Switch
                      mobileSpaceBetween={true}
                      onChange={(value) => {
                        if (appliedCreditFilterValues) {
                          setCreditFilterEnabled(value)
                        } else {
                          openCreditFilterForm()
                        }
                      }}
                      checked={creditFilterEnabled}
                    />

                    <Label>
                      {creditFilterFormI18n.creditFilter}
                      <EditText onClick={openCreditFilterForm}>
                        {appliedCreditFilterValues
                          ? `${commonI18n.to} ${formatPrice(appliedCreditFilterValues.creditPayment)}/${
                              commonI18n.monthForms[0]
                            }`
                          : creditFilterFormI18n.changeCreditParameters}
                      </EditText>
                    </Label>
                  </SwitchWrapper>
                )
              : isCreditFilterVisible && (
                  <SkeletonWrapper>
                    <SkeletonBlock width="100%" height="100%" />
                  </SkeletonWrapper>
                )}
            {state ? (
              tradeInAvailable &&
              isTradeInVisible && (
                <SwitchWrapper>
                  <Switch
                    mobileSpaceBetween={true}
                    onChange={(value) => {
                      if (value) {
                        if (state.tradeIn.behaviourByUser === 'not_required') {
                          api.enableTradeIn({ open: false })
                        } else {
                          api.enableTradeIn({ preventBack: true })
                        }
                      } else {
                        api.disableTradeIn()
                      }
                    }}
                    checked={state.tradeIn.behaviourByUser === 'applied'}
                  />

                  <Label>
                    {carsListI18n.tradeIn}
                    <EditText onClick={() => api.showTradeIn({ preventBack: true })}>{editTradeInText}</EditText>
                  </Label>
                </SwitchWrapper>
              )
            ) : (
              <SkeletonWrapper>
                <SkeletonBlock width="100%" height="100%" />
              </SkeletonWrapper>
            )}
          </>
        )}
      </FiltersContainer>
      {sorting && (
        <SortWrapper>
          <GenericV2Sort {...sorting} />
        </SortWrapper>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.4rem;
  background: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return theme.palette?.black
    if (getProjectName() === 'tank') return '#2C3232'
    return theme.palette.white
  }};
  border-radius: ${() => {
    if (getProjectName() === 'avito') return '1.2rem'
    return
  }};
  padding: 1rem;
  padding-right: 0px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 30px;
  }
`

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
  }
`

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 1rem 2rem;
  border-radius: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    gap: 15px;
  }
`

const SortWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`

const Label = styled.p`
  margin: 0;
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => {
    if (getProjectName() === 'tank') return theme.palette.grey[500]
    if (getProjectName() === 'generic_v2') return '#D9D9D9'
    return 'inherit'
  }};
`

const SkeletonWrapper = styled.div`
  height: 100%;
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.palette.white};
  border: 1px solid ${(props) => props.theme.palette.grey[100]};
  border-radius: ${() => getProjectName() === 'avito' && '12px;'};

  @media (max-width: 768px) {
    width: 100%;
  }
`

const EditText = styled.span`
  display: block;
  margin-top: 0.4rem;
  margin-bottom: 0;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
`
